import styled, { ThemeProvider } from 'styled-components';
import { LayoutRegisterContext } from 'src/providers/LayoutRegisterProvider';
import { LayoutConfigContext } from 'src/providers/LayoutConfigProvider';
import { useContext, useEffect } from 'react';
import { dirRTL, redTheme } from 'src/styles/theme';
import { media } from 'src/styles/designs';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnouncementContent } from 'src/modules/announcement/components/AnnouncementContent';
import { makeTransition } from 'src/utils/motion/transitions';
import { darken, lighten } from 'polished';
import { useAnnouncement } from 'src/modules/announcement/hooks/useAnnouncement';

const desktopHeight = 75;
const mobileHeight = 98;

const FixedContainer = styled(motion.div)`
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  max-width: 100vw;
  min-height: ${mobileHeight}px;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  pointer-events: none;
  ${media.w.greaterThanOrEqual.desktop} {
    min-height: ${desktopHeight}px;
  }
`;

const Wrapper = styled.div<{ $background: string }>`
  min-height: ${mobileHeight}px;
  background: ${({ $background }) =>
    `linear-gradient(267.17deg, ${darken(0.2, $background)} -0.08%, ${lighten(
      0.1,
      $background,
    )} 98.8%)`};
  pointer-events: all;
  ${media.w.greaterThanOrEqual.desktop} {
    min-height: ${desktopHeight}px;
    clip-path: polygon(16rpx 0, 100% 0, 100% 100%, 0% 100%);
    max-width: 70vw;
    ${dirRTL} {
      clip-path: polygon(0 0, calc(100% - 16rpx) 0, 100% 100%, 0% 100%);
    }
  }
`;

const MobilePlaceholder = styled.div`
  ${media.w.lessThan.desktop} {
    height: ${mobileHeight}px;
  }
`;

const Announcement = () => {
  const announcement = useAnnouncement();

  const layoutRegisterContext = useContext(LayoutRegisterContext);
  const layoutConfigContext = useContext(LayoutConfigContext);

  const isDesktop = useMediaQuery(media.w.greaterThanOrEqual.desktop);
  const isVisible = !!layoutConfigContext?.announcement && announcement;

  useEffect(() => {
    if (isVisible) {
      if (isDesktop) {
        layoutRegisterContext?.registerComponent({
          id: 'desktopAnnouncement',
          placement: 'footer',
          position: 'fixed',
          height: desktopHeight,
        });
      } else {
        layoutRegisterContext?.registerComponent({
          id: 'mobileAnnouncement',
          placement: 'footer',
          position: 'fixed',
          height: mobileHeight,
        });
        layoutRegisterContext?.registerComponent({
          id: 'mobileAnnouncementPlaceholder',
          placement: 'footer',
          position: 'static',
          height: mobileHeight,
        });
      }

      return () => {
        layoutRegisterContext?.deregisterComponent('desktopAnnouncement');
        layoutRegisterContext?.deregisterComponent('mobileAnnouncement');
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, isVisible]);

  return (
    <ThemeProvider theme={redTheme}>
      {isVisible && <MobilePlaceholder />}
      <AnimatePresence initial={false}>
        {isVisible && (
          <FixedContainer
            {...makeTransition({
              from: 'bottom',
              to: 'bottom',
              fade: true,
              enterDelay: 0.2,
            })}
          >
            <Wrapper $background={announcement.background}>
              <AnnouncementContent {...announcement} />
            </Wrapper>
          </FixedContainer>
        )}
      </AnimatePresence>
    </ThemeProvider>
  );
};

export default Announcement;
